import React, { forwardRef, useState } from 'react';
import { Clock, BookOpen, CheckCircle2, Mic, Download, Loader2, Moon, GraduationCap, Share2, Video, Headphones } from 'lucide-react';
import { VOICE_CATEGORIES } from './VoiceServicesSection';
import { Button } from './ui/button';
import { exportQuoteToPDF } from '../utils/pdfExport';
import { toast } from 'sonner';

const VIUBUX_LOGO = 'https://customer-assets.emergentagent.com/job_libro-quote-gen/artifacts/pf9umyn8_5.png';
const PLACEHOLDER_COVER = 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?auto=format&fit=crop&q=80&w=300&h=400';

export const QuotePreview = forwardRef(({ 
  formData, 
  quote, 
  formatPrice, 
  formatDuration,
  currency = 'MXN',
  distributionAddon = null,
  booktrailerAddon = null,
  legalKitAddon = null,
  coverDesignAddon = null,
  brandingPackAddon = null,
  musicStockAddon = null,
  consultoriaAddon = null,
  qrDinamicoAddon = null,
  enlaceGoAddon = null,
  nfcVcardAddon = null,
  separadoresAddon = null,
  preventaAddon = null,
  bannerPromotionAddon = null,
  disenoWebBannerAddon = null,
  photoshootAddon = null,
  paginaAutorAddon = null,
  pautajeDigitalAddon = null,
  commissionFreeAddon = null,
  tarjetasAddon = null,
  separadoresImpresosAddon = null,
  envioAddon = null,
  addonsTotal = 0,
  voiceCloningCost = 0,
  talentoPremiumCost = 0,
  extraVoicesUltraCost = 0,
  extraVoicesUltra = 0,
  revisionAudioCost = 0,
  revisionAudio = 'rev_none',
  masterizacionCost = 0,
  masterizacion = 'master_none',
  multivozCost = 0,
  multivozActive = false,
  multivozVoces = 0,
  customDiscount = 0,
  customDiscountType = 'percent',
  customDiscountAmount = 0,
  showDiscountPerItem = false,
  viubuxConnectActive = false,
  viubuxConnectDiscountAmount = 0,
  viubuxConnectInscription = 0,
  vcCatalogoCost = 0,
  subtotalAfterDiscount = 0,
  ivaAmount = 0,
  finalTotalWithCustomDiscount = 0,
  podcastPackage = null,
  podcastAddons = [],
  podcastTotal = 0,
  podcastQuantity = 1,
  podcastRecordingHours = 0,
  hasValidAudiobookData = false,
  customerName = '',
  customerEmail = '',
  customerPhone = '',
  // Meditaciones props
  meditacionesPackage = null,
  meditacionesTotal = 0,
  meditacionesQuantity = 1,
  meditacionesRecordingHours = 0,
  // AudioCursos props
  audiocursosPackage = null,
  audiocursosTotal = 0,
  audiocursosQuantity = 1,
  audiocursosRecordingHours = 0,
  savedQuoteCode = '',
  projectName = '',
  projectCoverUrl = '',
  // Walking Podcast props
  walkingPodcastPackage = null,
  walkingPodcastTotal = 0,
  walkingPodcastQuantity = 1,
  walkingPodcastRecordingHours = 0,
  walkingSonidoDirectoTotal = 0,
  walkingAsistenteTotal = 0,
  // New sections
  rentaEquipoTotal = 0,
  rentaEquipoData = {},
  transmisionTotal = 0,
  transmisionData = {},
  sonidoDirectoTotal = 0,
  sonidoDirectoData = {},
  foroTotal = 0,
  foroData = {},
  interpretacionData = {},
  interpretacionTotal = 0,
  sonorizacionData = {},
  sonorizacionTotal = 0,
  podcastPostProduction = false,
  showCoproduccion = false,
  coproduccionPercent = 50,
  coproduccionAmount = 0,
  clientPayAmount = 0,
  exchangeRate = 17.5,
  editorialData = null,
  voiceServicesQuantities = {},
  voiceServicesTotal = 0,
  studioHours = {},
  audioPostHours = 0,
  studioFeesTotal = 0,
  handFreeRows = [],
  handFreeTotal = 0,
  settings = {}
}, ref) => {
  const [isExporting, setIsExporting] = useState(false);
  
  // Helper to render price with VC discount
  const showVCDiscount = viubuxConnectActive && showDiscountPerItem;
  const renderPrice = (price) => {
    if (!showVCDiscount || price <= 0) {
      return <span className="font-medium text-slate-800">{formatPrice(price)}</span>;
    }
    const discounted = price * 0.85;
    return (
      <span className="flex flex-col items-end">
        <span className="text-red-400 line-through text-xs">{formatPrice(price)}</span>
        <span className="font-bold text-green-600">{formatPrice(discounted)}</span>
      </span>
    );
  };

  const today = new Date().toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const handleShareWhatsApp = () => {
    const lines = [];
    lines.push('*Cotización Viubux*');
    if (savedQuoteCode) lines.push(`📋 No. ${savedQuoteCode}`);
    if (projectName) lines.push(`📋 Proyecto: ${projectName}`);
    if (customerName) lines.push(`👤 Cliente: ${customerName}`);
    lines.push('');
    lines.push('*Desglose:*');
    
    if (hasAudiobook && quote?.package) {
      lines.push(`🎧 Audiolibro ${quote.package.name} - ${formatPrice(quote.grandTotal)} ${currency}`);
    }
    if (voiceCloningCost > 0) lines.push(`  🎤 Clonación de Voz - ${formatPrice(voiceCloningCost)} ${currency}`);
    if (talentoPremiumCost > 0) lines.push(`  ⭐ Talento Premium - ${formatPrice(talentoPremiumCost)} ${currency}`);
    if (extraVoicesUltraCost > 0) lines.push(`  🎤 Voces Clonadas (${extraVoicesUltra}) - ${formatPrice(extraVoicesUltraCost)} ${currency}`);
    if (multivozCost > 0) lines.push(`  🎤 Multivoz (${multivozVoces} voz/es) - ${formatPrice(multivozCost)} ${currency}`);
    if (revisionAudioCost > 0) lines.push(`  🔍 Revisión de Audio - ${formatPrice(revisionAudioCost)} ${currency}`);
    if (masterizacionCost > 0) lines.push(`  🎛️ Masterización - ${formatPrice(masterizacionCost)} ${currency}`);
    
    // Individual addons
    if (distributionAddon?.price > 0) lines.push(`  ➕ ${distributionAddon.name} - ${formatPrice(distributionAddon.price)} ${currency}`);
    if (booktrailerAddon?.price > 0) lines.push(`  ➕ ${booktrailerAddon.name} - ${formatPrice(booktrailerAddon.price)} ${currency}`);
    if (legalKitAddon?.price > 0) lines.push(`  ➕ ${legalKitAddon.name} - ${formatPrice(legalKitAddon.price)} ${currency}`);
    if (coverDesignAddon?.price > 0) lines.push(`  ➕ ${coverDesignAddon.name} - ${formatPrice(coverDesignAddon.price)} ${currency}`);
    if (brandingPackAddon?.price > 0) lines.push(`  ➕ ${brandingPackAddon.name} - ${formatPrice(brandingPackAddon.price)} ${currency}`);
    if (musicStockAddon?.price > 0) lines.push(`  ➕ ${musicStockAddon.name} - ${formatPrice(musicStockAddon.price)} ${currency}`);
    if (consultoriaAddon?.price > 0) lines.push(`  ➕ ${consultoriaAddon.name} - ${formatPrice(consultoriaAddon.price)} ${currency}`);
    if (qrDinamicoAddon?.price > 0) lines.push(`  ➕ ${qrDinamicoAddon.name} - ${formatPrice(qrDinamicoAddon.price)} ${currency}`);
    if (enlaceGoAddon?.price > 0) lines.push(`  ➕ ${enlaceGoAddon.name} - ${formatPrice(enlaceGoAddon.price)} ${currency}`);
    if (nfcVcardAddon?.price > 0) lines.push(`  ➕ ${nfcVcardAddon.name} - ${formatPrice(nfcVcardAddon.price)} ${currency}`);
    if (separadoresAddon?.price > 0) lines.push(`  ➕ ${separadoresAddon.name} - ${formatPrice(separadoresAddon.price)} ${currency}`);
    if (preventaAddon?.price > 0) lines.push(`  ➕ ${preventaAddon.name} - ${formatPrice(preventaAddon.price)} ${currency}`);
    if (bannerPromotionAddon?.price > 0) lines.push(`  ➕ ${bannerPromotionAddon.name} - ${formatPrice(bannerPromotionAddon.price)} ${currency}`);
    if (disenoWebBannerAddon?.price > 0) lines.push(`  ➕ ${disenoWebBannerAddon.name} - ${formatPrice(disenoWebBannerAddon.price)} ${currency}`);
    if (photoshootAddon?.price > 0) lines.push(`  ➕ ${photoshootAddon.name} - ${formatPrice(photoshootAddon.price)} ${currency}`);
    if (paginaAutorAddon?.price > 0) lines.push(`  ➕ ${paginaAutorAddon.name} - ${formatPrice(paginaAutorAddon.price)} ${currency}`);
    if (pautajeDigitalAddon?.price > 0) lines.push(`  ➕ ${pautajeDigitalAddon.name} - ${formatPrice(pautajeDigitalAddon.price)} ${currency}`);
    if (commissionFreeAddon?.price > 0) lines.push(`  ➕ ${commissionFreeAddon.name} - ${formatPrice(commissionFreeAddon.price)} ${currency}`);
    if (tarjetasAddon?.price > 0) lines.push(`  ➕ ${tarjetasAddon.name} - ${formatPrice(tarjetasAddon.price)} ${currency}`);
    if (separadoresImpresosAddon?.price > 0) lines.push(`  ➕ ${separadoresImpresosAddon.name} - ${formatPrice(separadoresImpresosAddon.price)} ${currency}`);
    if (envioAddon?.price > 0) lines.push(`  ➕ ${envioAddon.name} - ${formatPrice(envioAddon.price)} ${currency}`);

    if (hasPodcast && podcastTotal > 0) lines.push(`🎙️ Podcast - ${formatPrice(podcastTotal)} ${currency}`);
    if (hasWalkingPodcast && walkingPodcastTotal > 0) lines.push(`🚶 Walking Podcast - ${formatPrice(walkingPodcastTotal)} ${currency}`);
    if (hasMeditaciones && meditacionesTotal > 0) lines.push(`🧘 Meditaciones - ${formatPrice(meditacionesTotal)} ${currency}`);
    if (hasAudiocursos && audiocursosTotal > 0) lines.push(`📚 Cursos (A/V) - ${formatPrice(audiocursosTotal)} ${currency}`);
    if (hasRentaEquipo && rentaEquipoTotal > 0) lines.push(`📦 Renta de Equipo - ${formatPrice(rentaEquipoTotal)} ${currency}`);
    if (hasTransmision && transmisionTotal > 0) lines.push(`📡 Transmisión en Vivo - ${formatPrice(transmisionTotal)} ${currency}`);
    if (hasSonidoDirecto && sonidoDirectoTotal > 0) lines.push(`🎚️ Sonido Directo - ${formatPrice(sonidoDirectoTotal)} ${currency}`);
    if (hasForo && foroTotal > 0) lines.push(`🏛️ Foro - ${formatPrice(foroTotal)} ${currency}`);
    if (hasInterpretacion && interpretacionTotal > 0) lines.push(`🌐 Interpretación Simultánea - ${formatPrice(interpretacionTotal)} ${currency}`);
    if (hasSonorizacion && sonorizacionTotal > 0) lines.push(`🔊 Sonorización - ${formatPrice(sonorizacionTotal)} ${currency}`);
    if (hasEditorial && editorialData) lines.push(`📖 Servicios Editoriales - incluidos`);

    if (voiceServicesTotal > 0) {
      lines.push(`🎤 Servicios de Voz - ${formatPrice(voiceServicesTotal)} ${currency}`);
    }
    if (studioFeesTotal > 0) {
      lines.push(`🏠 Studio Fees - ${formatPrice(studioFeesTotal)} ${currency}`);
    }
    if (handFreeTotal > 0) {
      lines.push(`📋 Hand-Free - ${formatPrice(handFreeTotal)} ${currency}`);
    }

    if (viubuxConnectActive) {
      lines.push(`💎 ViubuxConnect - +${formatPrice(viubuxConnectInscription)} ${currency}`);
      if (viubuxConnectDiscountAmount > 0) lines.push(`  🏷️ Descuento ViubuxConnect (15%) - -${formatPrice(viubuxConnectDiscountAmount)} ${currency}`);
    }
    
    lines.push('');
    lines.push(`*Subtotal:* ${formatPrice(subtotalAfterDiscount)} ${currency}`);
    lines.push(`*IVA (16%):* ${formatPrice(ivaAmount)} ${currency}`);
    if (customDiscount > 0) lines.push(`*Descuento:* -${customDiscountType === 'fixed' ? formatPrice(customDiscount) : `${customDiscount}%`}`);
    lines.push(`*TOTAL: ${formatPrice(finalTotalWithCustomDiscount)} ${currency}*`);
    lines.push('');

    if (savedQuoteCode) {
      lines.push(`🔗 Visualiza tu cotización: https://cotizaciones.viubux.com?code=${savedQuoteCode}`);
      lines.push('');
      lines.push('🔐 *Para editar tu cotización online:*');
      if (customerEmail) lines.push(`   Correo: ${customerEmail}`);
      lines.push('   Contraseña: Viubux4u');
      lines.push('');
    }

    lines.push('www.viubux.com');

    window.open(`https://wa.me/?text=${encodeURIComponent(lines.join('\n'))}`, '_blank');
  };

  const validityDate = new Date();
  validityDate.setDate(validityDate.getDate() + 30);
  const validUntil = validityDate.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Check if any service is selected
  const hasAudiobook = hasValidAudiobookData && quote;
  const hasPodcast = podcastPackage !== null && podcastPackage.id !== null && podcastPackage.price > 0;
  const hasMeditaciones = meditacionesPackage !== null && meditacionesTotal > 0;
  const hasAudiocursos = audiocursosPackage !== null && audiocursosTotal > 0;
  const hasWalkingPodcast = walkingPodcastPackage !== null && walkingPodcastTotal > 0;
  const hasIndependentAddons = (addonsTotal > 0) && !hasAudiobook;
  const hasEditorial = editorialData && editorialData.subtotal > 0;
  const hasRentaEquipo = rentaEquipoTotal > 0;
  const hasTransmision = transmisionTotal > 0;
  const hasSonidoDirecto = sonidoDirectoTotal > 0;
  const hasForo = foroTotal > 0;
  const hasInterpretacion = interpretacionTotal > 0;
  const hasSonorizacion = sonorizacionTotal > 0;
  const hasAnyService = hasAudiobook || hasPodcast || hasMeditaciones || hasAudiocursos || hasWalkingPodcast || hasIndependentAddons || hasEditorial || hasRentaEquipo || hasTransmision || hasSonidoDirecto || hasForo || hasInterpretacion || hasSonorizacion || voiceServicesTotal > 0 || studioFeesTotal > 0 || handFreeTotal > 0;

  // Handle PDF export
  const handleExportPDF = async () => {
    if (!ref?.current) {
      toast.error('No se puede generar el PDF');
      return;
    }
    
    setIsExporting(true);
    try {
      // Collect all addons for the PDF
      const addonsData = [];
      if (distributionAddon?.price > 0) {
        addonsData.push({ name: distributionAddon.name, price: distributionAddon.price, quantity: 1 });
      }
      if (booktrailerAddon?.price > 0) {
        addonsData.push({ name: booktrailerAddon.name, price: booktrailerAddon.price, quantity: 1 });
      }
      if (legalKitAddon?.price > 0) {
        addonsData.push({ name: legalKitAddon.name, price: legalKitAddon.price, quantity: 1 });
      }
      if (coverDesignAddon?.price > 0) {
        addonsData.push({ name: coverDesignAddon.name, price: coverDesignAddon.price, quantity: 1 });
      }
      if (brandingPackAddon?.price > 0) {
        addonsData.push({ name: brandingPackAddon.name, price: brandingPackAddon.price, quantity: 1 });
      }
      if (musicStockAddon?.price > 0) {
        addonsData.push({ name: musicStockAddon.name, price: musicStockAddon.price, quantity: musicStockAddon.quantity || 1 });
      }
      if (consultoriaAddon?.price > 0) {
        addonsData.push({ name: consultoriaAddon.name, price: consultoriaAddon.price, quantity: consultoriaAddon.quantity || 1 });
      }
      // New addons
      if (qrDinamicoAddon?.price > 0) {
        addonsData.push({ name: qrDinamicoAddon.name, price: qrDinamicoAddon.price, quantity: 1 });
      }
      if (enlaceGoAddon?.price > 0) {
        addonsData.push({ name: enlaceGoAddon.name, price: enlaceGoAddon.price, quantity: 1 });
      }
      if (nfcVcardAddon?.price > 0) {
        addonsData.push({ name: nfcVcardAddon.name, price: nfcVcardAddon.price, quantity: 1 });
      }
      if (separadoresAddon?.price > 0) {
        addonsData.push({ name: separadoresAddon.name, price: separadoresAddon.price, quantity: 1 });
      }
      if (preventaAddon?.price > 0) {
        addonsData.push({ name: preventaAddon.name, price: preventaAddon.price, quantity: 1 });
      }
      if (bannerPromotionAddon?.price > 0) {
        addonsData.push({ name: bannerPromotionAddon.name, price: bannerPromotionAddon.price, quantity: 1 });
      }
      if (disenoWebBannerAddon?.price > 0) {
        addonsData.push({ name: disenoWebBannerAddon.name, price: disenoWebBannerAddon.price, quantity: 1 });
      }
      if (photoshootAddon?.price > 0) {
        addonsData.push({ name: photoshootAddon.name, price: photoshootAddon.price, quantity: 1 });
      }
      if (paginaAutorAddon?.price > 0) {
        addonsData.push({ name: paginaAutorAddon.name, price: paginaAutorAddon.price, quantity: 1 });
      }
      if (pautajeDigitalAddon?.price > 0) {
        addonsData.push({ name: pautajeDigitalAddon.name, price: pautajeDigitalAddon.price, quantity: 1 });
      }
      if (commissionFreeAddon?.price > 0) {
        addonsData.push({ name: commissionFreeAddon.name, price: commissionFreeAddon.price, quantity: 1 });
      }
      if (tarjetasAddon?.price > 0) {
        addonsData.push({ name: tarjetasAddon.name, price: tarjetasAddon.price, quantity: 1 });
      }
      if (separadoresImpresosAddon?.price > 0) {
        addonsData.push({ name: separadoresImpresosAddon.name, price: separadoresImpresosAddon.price, quantity: 1 });
      }
      if (envioAddon?.price > 0) {
        addonsData.push({ name: envioAddon.name, price: envioAddon.price, quantity: 1 });
      }

      // Calculate totals
      const audiobookSubtotal = hasAudiobook ? (quote?.grandTotal || 0) + addonsTotal + voiceCloningCost + talentoPremiumCost + extraVoicesUltraCost + revisionAudioCost + masterizacionCost + multivozCost : 0;
      const podcastSubtotal = hasPodcast ? podcastTotal : 0;
      const meditacionesSubtotal = hasMeditaciones ? meditacionesTotal : 0;
      const audiocursosSubtotal = hasAudiocursos ? audiocursosTotal : 0;
      const walkingPodcastSubtotal = hasWalkingPodcast ? walkingPodcastTotal : 0;
      const addonsSubtotal = hasIndependentAddons ? addonsTotal : 0;
      
      const quoteDataForPDF = {
        bookTitle: formData?.bookTitle,
        customerName: customerName,
        customerEmail: customerEmail,
        customerPhone: customerPhone,
        coverUrl: formData?.coverUrl,
        // Audiobook data
        audiobookData: hasAudiobook ? {
          bookTitle: formData?.bookTitle,
          authorName: formData?.authorName,
          packageName: quote?.package?.name,
          durationHours: quote?.estimatedDurationHours,
          wordCount: formData?.wordCount,
          pageCount: formData?.pageCount,
          basePrice: quote?.basePrice,
          volumeDiscount: quote?.volumeDiscountPercent,
          manualDiscount: quote?.manualDiscountPercent,
          finalPricePerHour: quote?.finalPrice,
          subtotal: quote?.grandTotal
        } : null,
        voiceCloningCost, talentoPremiumCost, extraVoicesUltraCost, extraVoicesUltra, multivozCost, multivozActive, multivozVoces, revisionAudioCost, revisionAudio, masterizacionCost, masterizacion,
        // Podcast data
        podcastData: hasPodcast ? {
          packageName: podcastPackage?.name,
          packageDescription: podcastPackage?.description,
          quantity: podcastQuantity,
          recordingHours: podcastRecordingHours,
          pricePerUnit: podcastPackage?.price,
          addons: podcastAddons?.map(a => a.name) || [],
          subtotal: podcastTotal,
          postProduction: podcastPostProduction
        } : null,
        // Meditaciones data
        meditacionesData: hasMeditaciones ? {
          packageName: meditacionesPackage?.name,
          quantity: meditacionesQuantity,
          recordingHours: meditacionesRecordingHours,
          pricePerUnit: meditacionesPackage?.price,
          subtotal: meditacionesTotal
        } : null,
        // AudioCursos data
        audiocursosData: hasAudiocursos ? {
          packageName: audiocursosPackage?.name,
          quantity: audiocursosQuantity,
          recordingHours: audiocursosRecordingHours,
          pricePerUnit: audiocursosPackage?.price,
          subtotal: audiocursosTotal
        } : null,
        // Walking Podcast data
        walkingPodcastData: hasWalkingPodcast ? {
          packageName: walkingPodcastPackage?.name,
          quantity: walkingPodcastQuantity,
          recordingHours: walkingPodcastRecordingHours,
          pricePerUnit: walkingPodcastPackage?.price,
          sonidoDirectoTotal: walkingSonidoDirectoTotal,
          asistenteTotal: walkingAsistenteTotal,
          subtotal: walkingPodcastTotal
        } : null,
        // Addons data
        addonsData: addonsData.length > 0 ? addonsData : null,
        // Editorial data
        editorialData: hasEditorial ? editorialData : null,
        // New sections data
        rentaEquipoData: hasRentaEquipo ? rentaEquipoData : null,
        rentaEquipoTotal: rentaEquipoTotal,
        transmisionData: hasTransmision ? transmisionData : null,
        transmisionTotal: transmisionTotal,
        sonidoDirectoData: hasSonidoDirecto ? sonidoDirectoData : null,
        sonidoDirectoTotal: sonidoDirectoTotal,
        foroData: hasForo ? foroData : null,
        foroTotal: foroTotal,
        interpretacionData: hasInterpretacion ? interpretacionData : null,
        interpretacionTotal: interpretacionTotal,
        sonorizacionData: hasSonorizacion ? sonorizacionData : null,
        sonorizacionTotal: sonorizacionTotal,
        // Totals
        totals: {
          audiobookSubtotal: audiobookSubtotal,
          podcastSubtotal: podcastSubtotal,
          meditacionesSubtotal: meditacionesSubtotal,
          audiocursosSubtotal: audiocursosSubtotal,
          addonsSubtotal: addonsSubtotal,
          subtotal: subtotalAfterDiscount + customDiscountAmount,
          customDiscount: customDiscount,
          customDiscountType: customDiscountType,
          discountAmount: customDiscountAmount,
          iva: ivaAmount,
          grandTotal: finalTotalWithCustomDiscount,
          deposit: finalTotalWithCustomDiscount * 0.1,
          showCoproduccion: showCoproduccion,
          coproduccionPercent: coproduccionPercent,
          coproduccionAmount: coproduccionAmount,
          clientPayAmount: clientPayAmount,
          viubuxConnectActive: viubuxConnectActive,
          showDiscountPerItem: showDiscountPerItem,
          viubuxConnectInscription: viubuxConnectInscription,
          viubuxConnectDiscountAmount: viubuxConnectDiscountAmount,
          vcCatalogoCost: vcCatalogoCost
        },
        quoteNumber: savedQuoteCode || null,
        voiceServicesQuantities: voiceServicesQuantities,
        voiceServicesTotal: voiceServicesTotal,
        studioHours: studioHours,
        audioPostHours: audioPostHours,
        studioFeesTotal: studioFeesTotal,
        handFreeRows: handFreeRows,
        handFreeTotal: handFreeTotal,
        projectName: projectName || null,
        projectCoverUrl: projectCoverUrl || null,
        currency: currency,
        exchangeRate: exchangeRate
      };

      await exportQuoteToPDF(ref.current, quoteDataForPDF);
      toast.success('PDF descargado correctamente');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Error al generar el PDF');
    } finally {
      setIsExporting(false);
    }
  };

  if (!hasAnyService) {
    return (
      <div 
        ref={ref}
        className="bg-white rounded-xl shadow-2xl p-6 min-h-[400px] flex items-center justify-center"
        data-testid="quote-preview-empty"
      >
        <div className="text-center text-slate-400">
          <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-base">Selecciona un servicio para ver la cotización</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={ref}
      className="bg-white rounded-xl shadow-2xl overflow-hidden text-sm"
      data-testid="quote-preview"
    >
      {/* Header with gradient */}
      <div className="bg-gradient-to-r from-[#aa2aff] to-[#272b96] px-6 py-4">
        <div className="flex justify-between items-start">
          <img 
            src={VIUBUX_LOGO} 
            alt="Viubux" 
            className="h-8 object-contain"
          />
          <div className="text-right text-white">
            <p className="text-xs opacity-80">Cotización</p>
            {savedQuoteCode && (
              <p className="text-sm font-mono font-bold tracking-wider">{savedQuoteCode}</p>
            )}
            <p className="text-xs opacity-60">{today}</p>
          </div>
        </div>
      </div>

      {/* Project Name Header + Cover */}
      {(projectName || projectCoverUrl) && (
        <div className="px-5 pt-4 pb-3 border-b border-slate-100">
          {projectCoverUrl && !hasAudiobook && (
            <div className="flex justify-center mb-3">
              <img
                src={projectCoverUrl}
                alt="Portada del proyecto"
                className="max-h-32 rounded-lg shadow-md object-contain"
                data-testid="project-cover-preview"
              />
            </div>
          )}
          {projectName && (
            <h2 className="text-lg font-bold text-[#272b96] text-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {projectName}
            </h2>
          )}
        </div>
      )}

      {/* Export PDF Button */}
      <div className="px-5 pt-4 pb-2 border-b border-slate-100 print:hidden flex gap-2">
        <Button
          onClick={handleExportPDF}
          disabled={isExporting}
          variant="outline"
          size="sm"
          className="flex-1 border-[#aa2aff]/30 text-[#aa2aff] hover:bg-[#aa2aff]/10 hover:border-[#aa2aff] bg-white"
          data-testid="btn-export-pdf"
        >
          {isExporting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generando...
            </>
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              Descargar PDF
            </>
          )}
        </Button>
        <Button
          onClick={handleShareWhatsApp}
          variant="outline"
          size="sm"
          className="flex-1 border-green-500/30 text-green-600 hover:bg-green-500/10 hover:border-green-500 bg-white"
          data-testid="btn-share-whatsapp"
        >
          <Share2 className="w-4 h-4 mr-2" />
          WhatsApp
        </Button>
      </div>

      <div className="p-5">
        {/* Audiobook Section */}
        {hasAudiobook && (
          <div className="mb-5">
            {/* Book Info Section */}
            <div className="flex gap-4 mb-4">
              <img
                src={formData.coverUrl || PLACEHOLDER_COVER}
                alt="Book cover"
                className="w-20 h-28 object-cover rounded-lg shadow-md flex-shrink-0"
              />
              <div className="flex-grow min-w-0">
                <h2 className="text-lg font-bold text-slate-800 mb-0.5 truncate" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {formData.bookTitle || 'Título del Libro'}
                </h2>
                <p className="text-slate-600 text-sm mb-2">
                  por <span className="font-medium">{formData.authorName || 'Autor'}</span>
                </p>
                
                <div className="flex gap-3 text-xs">
                  <div className="flex items-center gap-1 text-slate-500">
                    <BookOpen className="w-3 h-3" />
                    <span>{formData.pageCount || 0} págs</span>
                  </div>
                  <div className="flex items-center gap-1 text-slate-500">
                    <Clock className="w-3 h-3" />
                    <span>{formatDuration(quote.estimatedDurationMinutes)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Package Selected */}
            <div className="p-3 bg-gradient-to-r from-[#0f133a] to-[#1a1f50] rounded-lg text-white mb-3">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-sm font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Paquete {quote.package.name}
                </h3>
              </div>
              <ul className="grid grid-cols-2 gap-1">
                {quote.package.features.slice(0, 4).map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-1 text-xs text-slate-300">
                    <CheckCircle2 className="w-3 h-3 text-[#aa2aff] flex-shrink-0 mt-0.5" />
                    <span className="line-clamp-1">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Audiobook Pricing */}
            <div className="border-t border-slate-200 pt-3">
              <table className="w-full text-xs">
                <tbody>
                  <tr>
                    <td className="py-1 text-slate-600">Precio/hora ({quote.package.name})</td>
                    <td className="py-1 text-right font-medium text-slate-800">
                      {formatPrice(quote.finalPrice)}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-1 text-slate-600">
                      Duración × Precio ({quote.estimatedDurationHours.toFixed(1)}h × {formatPrice(quote.finalPrice)})
                    </td>
                    <td className="py-1 text-right font-medium text-slate-800">
                      {formatPrice(quote.grandTotal)}
                    </td>
                  </tr>
                  {/* Distribution Addon */}
                  {distributionAddon?.price > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">{distributionAddon.name}</td>
                      <td className="py-1 text-right">{renderPrice(distributionAddon.price)}</td>
                    </tr>
                  )}
                  
                  {/* Booktrailer Addon */}
                  {booktrailerAddon?.price > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">{booktrailerAddon.name}</td>
                      <td className="py-1 text-right">{renderPrice(booktrailerAddon.price)}</td>
                    </tr>
                  )}
                  
                  {/* Legal Kit Addon */}
                  {legalKitAddon?.price > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">{legalKitAddon.name}</td>
                      <td className="py-1 text-right">{renderPrice(legalKitAddon.price)}</td>
                    </tr>
                  )}
                  
                  {/* Cover Design Addon */}
                  {coverDesignAddon?.price > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">{coverDesignAddon.name}</td>
                      <td className="py-1 text-right">{renderPrice(coverDesignAddon.price)}</td>
                    </tr>
                  )}
                  
                  {/* Branding Pack Addon */}
                  {brandingPackAddon?.price > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">{brandingPackAddon.name}</td>
                      <td className="py-1 text-right">{renderPrice(brandingPackAddon.price)}</td>
                    </tr>
                  )}
                  
                  {/* Music Stock Addon */}
                  {musicStockAddon?.price > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">{musicStockAddon.name}</td>
                      <td className="py-1 text-right">{renderPrice(musicStockAddon.price)}</td>
                    </tr>
                  )}

                  {/* Consultoría */}
                  {consultoriaAddon?.price > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">{consultoriaAddon.name}</td>
                      <td className="py-1 text-right">{renderPrice(consultoriaAddon.price)}</td>
                    </tr>
                  )}

                  {/* QR Dinámico */}
                  {qrDinamicoAddon?.price > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">{qrDinamicoAddon.name}</td>
                      <td className="py-1 text-right">{renderPrice(qrDinamicoAddon.price)}</td>
                    </tr>
                  )}

                  {/* Enlace Go.Viubux */}
                  {enlaceGoAddon?.price > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">{enlaceGoAddon.name}</td>
                      <td className="py-1 text-right">{renderPrice(enlaceGoAddon.price)}</td>
                    </tr>
                  )}

                  {/* NFC VCard */}
                  {nfcVcardAddon?.price > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">{nfcVcardAddon.name}</td>
                      <td className="py-1 text-right">{renderPrice(nfcVcardAddon.price)}</td>
                    </tr>
                  )}

                  {/* Separadores */}
                  {separadoresAddon?.price > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">{separadoresAddon.name}</td>
                      <td className="py-1 text-right">{renderPrice(separadoresAddon.price)}</td>
                    </tr>
                  )}

                  {/* Preventa */}
                  {preventaAddon?.price > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">{preventaAddon.name}</td>
                      <td className="py-1 text-right">{renderPrice(preventaAddon.price)}</td>
                    </tr>
                  )}

                  {/* Banner Promotion */}
                  {bannerPromotionAddon?.price > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">{bannerPromotionAddon.name}</td>
                      <td className="py-1 text-right">{renderPrice(bannerPromotionAddon.price)}</td>
                    </tr>
                  )}

                  {/* Diseño WEB Banner */}
                  {disenoWebBannerAddon?.price > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">{disenoWebBannerAddon.name}</td>
                      <td className="py-1 text-right">{renderPrice(disenoWebBannerAddon.price)}</td>
                    </tr>
                  )}

                  {/* Photoshoot */}
                  {photoshootAddon?.price > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">{photoshootAddon.name}</td>
                      <td className="py-1 text-right">{renderPrice(photoshootAddon.price)}</td>
                    </tr>
                  )}

                  {/* Página de Autor */}
                  {paginaAutorAddon?.price > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">{paginaAutorAddon.name}</td>
                      <td className="py-1 text-right">{renderPrice(paginaAutorAddon.price)}</td>
                    </tr>
                  )}

                  {/* Pautaje Digital */}
                  {pautajeDigitalAddon?.price > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">{pautajeDigitalAddon.name}</td>
                      <td className="py-1 text-right">{renderPrice(pautajeDigitalAddon.price)}</td>
                    </tr>
                  )}

                  {/* Commission-Free Monthly Pass */}
                  {commissionFreeAddon?.price > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">{commissionFreeAddon.name}</td>
                      <td className="py-1 text-right">{renderPrice(commissionFreeAddon.price)}</td>
                    </tr>
                  )}

                  {/* Tarjetas */}
                  {tarjetasAddon?.price > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">{tarjetasAddon.name}</td>
                      <td className="py-1 text-right">{renderPrice(tarjetasAddon.price)}</td>
                    </tr>
                  )}

                  {/* Separadores Impresos */}
                  {separadoresImpresosAddon?.price > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">{separadoresImpresosAddon.name}</td>
                      <td className="py-1 text-right">{renderPrice(separadoresImpresosAddon.price)}</td>
                    </tr>
                  )}

                  {/* Envío */}
                  {envioAddon?.price > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">{envioAddon.name}</td>
                      <td className="py-1 text-right">{renderPrice(envioAddon.price)}</td>
                    </tr>
                  )}

                  {voiceCloningCost > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">Clonacion de Voz Original (pago unico)</td>
                      <td className="py-1 text-right">{renderPrice(voiceCloningCost)}</td>
                    </tr>
                  )}

                  {talentoPremiumCost > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">Upgrade Talento Premium</td>
                      <td className="py-1 text-right">{renderPrice(talentoPremiumCost)}</td>
                    </tr>
                  )}

                  {extraVoicesUltraCost > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">Voces Clonadas Adicionales ({extraVoicesUltra})</td>
                      <td className="py-1 text-right">{renderPrice(extraVoicesUltraCost)}</td>
                    </tr>
                  )}

                  {multivozCost > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">Multivoz ({multivozVoces} voz/es)</td>
                      <td className="py-1 text-right">{renderPrice(multivozCost)}</td>
                    </tr>
                  )}

                  {revisionAudioCost > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">Revision de Audio para Distribucion</td>
                      <td className="py-1 text-right">{renderPrice(revisionAudioCost)}</td>
                    </tr>
                  )}

                  {masterizacionCost > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">Masterizacion de Audiolibro</td>
                      <td className="py-1 text-right">{renderPrice(masterizacionCost)}</td>
                    </tr>
                  )}
                  
                  <tr className="border-t border-slate-200">
                    <td className="py-2 font-semibold text-slate-700">Subtotal Audiolibro</td>
                    <td className="py-2 text-right font-bold text-slate-800">
                      {formatPrice(quote.grandTotal + addonsTotal + voiceCloningCost + talentoPremiumCost + extraVoicesUltraCost + multivozCost)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Revisión de Audio y Masterización - solo cuando NO hay audiolibro */}
        {!hasAudiobook && (revisionAudioCost > 0 || masterizacionCost > 0) && (
          <div className={`${hasAudiobook ? 'border-t border-slate-200 pt-4' : ''} mb-4`}>
            <div className="flex items-center gap-2 mb-3">
              <Headphones className="w-4 h-4 text-[#aa2aff]" />
              <h3 className="text-sm font-bold text-slate-800" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Servicios de Audio
              </h3>
            </div>
            <table className="w-full text-xs">
              <tbody>
                {revisionAudioCost > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">Revisión de Archivos de Audio</td>
                    <td className="py-1 text-right font-medium text-slate-800">{formatPrice(revisionAudioCost)}</td>
                  </tr>
                )}
                {masterizacionCost > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">Masterización de Audiolibro</td>
                    <td className="py-1 text-right font-medium text-slate-800">{formatPrice(masterizacionCost)}</td>
                  </tr>
                )}
                <tr className="border-t border-slate-200">
                  <td className="py-2 font-semibold text-slate-700">Subtotal Servicios de Audio</td>
                  <td className="py-2 text-right font-bold text-slate-800">{formatPrice(revisionAudioCost + masterizacionCost)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Podcast Section */}
        {hasPodcast && (
          <div className={`${hasAudiobook ? 'border-t border-slate-200 pt-4' : ''} mb-4`}>
            <div className="flex items-center gap-2 mb-3">
              <Mic className="w-4 h-4 text-[#aa2aff]" />
              <h3 className="text-sm font-bold text-slate-800" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Podcast
              </h3>
              {podcastQuantity > 1 && (
                <span className="text-xs text-slate-500">({podcastQuantity} programas)</span>
              )}
            </div>
            
            <table className="w-full text-xs">
              <tbody>
                {podcastRecordingHours > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">Horas de Grabación Estimadas</td>
                    <td className="py-1 text-right font-medium text-slate-800">
                      {podcastRecordingHours.toFixed(1)} hrs
                    </td>
                  </tr>
                )}
                <tr>
                  <td className="py-1 text-slate-600">
                    Paquete {podcastPackage.name}{podcastPostProduction ? ' + Post-Producción' : ''}
                    <span className="block text-xs text-slate-400">
                      {podcastPostProduction
                        ? (podcastPackage.description || '').replace(/no incluye post-producción\.?/i, 'Incluye Post-Producción. Materiales terminados entregados vía nube tras finiquito.')
                        : podcastPackage.description}
                    </span>
                  </td>
                  <td className="py-1 text-right font-medium text-slate-800 align-top">
                    {formatPrice(podcastPackage.price * (podcastPostProduction ? 2 : 1))}{podcastRecordingHours > 0 ? '/hr' : ''} {podcastRecordingHours > 0 ? `× ${podcastRecordingHours.toFixed(1)}` : podcastQuantity > 1 ? `× ${podcastQuantity}` : ''}
                  </td>
                </tr>
                {podcastAddons.map(addon => (
                  <tr key={addon.id}>
                    <td className="py-1 text-slate-600">{addon.name}</td>
                    <td className="py-1 text-right font-medium text-slate-800">
                      +{formatPrice(addon.price)} {podcastQuantity > 1 ? `× ${podcastQuantity}` : ''}
                    </td>
                  </tr>
                ))}
                <tr className="border-t border-slate-200">
                  <td className="py-2 font-semibold text-slate-700">Subtotal Podcast</td>
                  <td className="py-2 text-right font-bold text-slate-800">
                    {formatPrice(podcastTotal)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Meditaciones Guiadas Section */}
        {hasMeditaciones && (
          <div className={`${hasAudiobook || hasPodcast ? 'border-t border-slate-200 pt-4' : ''} mb-4`}>
            <div className="flex items-center gap-2 mb-3">
              <Moon className="w-4 h-4 text-[#aa2aff]" />
              <h3 className="text-sm font-bold text-slate-800" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Meditaciones Guiadas
              </h3>
              {meditacionesQuantity > 1 && (
                <span className="text-xs text-slate-500">({meditacionesQuantity} sesiones)</span>
              )}
            </div>
            
            <table className="w-full text-xs">
              <tbody>
                {meditacionesRecordingHours > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">Horas de Producción Estimadas</td>
                    <td className="py-1 text-right font-medium text-slate-800">
                      {meditacionesRecordingHours.toFixed(1)} hrs
                    </td>
                  </tr>
                )}
                <tr>
                  <td className="py-1 text-slate-600">
                    Paquete {meditacionesPackage.name}
                  </td>
                  <td className="py-1 text-right font-medium text-slate-800">
                    {formatPrice(meditacionesPackage.price)}
                    {meditacionesRecordingHours > 0 && '/hr'}
                  </td>
                </tr>
                {meditacionesRecordingHours > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">Cantidad</td>
                    <td className="py-1 text-right font-medium text-slate-800">
                      × {meditacionesRecordingHours.toFixed(1)} hrs
                    </td>
                  </tr>
                )}
                <tr className="border-t border-slate-200">
                  <td className="py-2 font-semibold text-slate-700">Subtotal Meditaciones</td>
                  <td className="py-2 text-right font-bold text-slate-800">
                    {formatPrice(meditacionesTotal)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* AudioCursos Section */}
        {hasAudiocursos && (
          <div className={`${hasAudiobook || hasPodcast || hasMeditaciones ? 'border-t border-slate-200 pt-4' : ''} mb-4`}>
            <div className="flex items-center gap-2 mb-3">
              <GraduationCap className="w-4 h-4 text-[#aa2aff]" />
              <h3 className="text-sm font-bold text-slate-800" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Cursos (A/V)
              </h3>
              {audiocursosQuantity > 1 && (
                <span className="text-xs text-slate-500">({audiocursosQuantity} lecciones)</span>
              )}
            </div>
            
            <table className="w-full text-xs">
              <tbody>
                {audiocursosRecordingHours > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">Horas de Grabación Estimadas</td>
                    <td className="py-1 text-right font-medium text-slate-800">
                      {audiocursosRecordingHours.toFixed(1)} hrs
                    </td>
                  </tr>
                )}
                <tr>
                  <td className="py-1 text-slate-600">
                    Paquete {audiocursosPackage.name}
                  </td>
                  <td className="py-1 text-right font-medium text-slate-800">
                    {formatPrice(audiocursosPackage.price)}
                    {audiocursosRecordingHours > 0 && '/hr'}
                  </td>
                </tr>
                {audiocursosRecordingHours > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">Cantidad</td>
                    <td className="py-1 text-right font-medium text-slate-800">
                      × {audiocursosRecordingHours.toFixed(1)} hrs
                    </td>
                  </tr>
                )}
                <tr className="border-t border-slate-200">
                  <td className="py-2 font-semibold text-slate-700">Subtotal Cursos (A/V)</td>
                  <td className="py-2 text-right font-bold text-slate-800">
                    {formatPrice(audiocursosTotal)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Walking Podcast Section */}
        {hasWalkingPodcast && (
          <div className={`${hasAudiobook || hasPodcast || hasMeditaciones || hasAudiocursos ? 'border-t border-slate-200 pt-4' : ''} mb-4`}>
            <div className="flex items-center gap-2 mb-3">
              <Video className="w-4 h-4 text-[#aa2aff]" />
              <h3 className="text-sm font-bold text-slate-800" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Walking Podcast
              </h3>
              {walkingPodcastQuantity > 1 && (
                <span className="text-xs text-slate-500">({walkingPodcastQuantity} programas)</span>
              )}
            </div>
            
            <table className="w-full text-xs">
              <tbody>
                {walkingPodcastRecordingHours > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">Horas de Grabación Estimadas</td>
                    <td className="py-1 text-right font-medium text-slate-800">
                      {walkingPodcastRecordingHours.toFixed(1)} hrs
                    </td>
                  </tr>
                )}
                <tr>
                  <td className="py-1 text-slate-600">
                    Paquete {walkingPodcastPackage.name}
                  </td>
                  <td className="py-1 text-right font-medium text-slate-800">
                    {formatPrice(walkingPodcastPackage.price)}
                    {walkingPodcastRecordingHours > 0 && '/hr'}
                  </td>
                </tr>
                {walkingSonidoDirectoTotal > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">Sonido Directo</td>
                    <td className="py-1 text-right">{renderPrice(walkingSonidoDirectoTotal)}</td>
                  </tr>
                )}
                {walkingAsistenteTotal > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">Asistente</td>
                    <td className="py-1 text-right">{renderPrice(walkingAsistenteTotal)}</td>
                  </tr>
                )}
                <tr className="border-t border-slate-200">
                  <td className="py-2 font-semibold text-slate-700">Subtotal Walking Podcast</td>
                  <td className="py-2 text-right font-bold text-slate-800">
                    {formatPrice(walkingPodcastTotal)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Renta de Equipo, Transmisión, Sonido Directo, Foro */}
        {hasRentaEquipo && (
          <div className="border-t border-slate-200 pt-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-sm font-bold text-slate-800">Renta de Equipo</h3>
            </div>
            <table className="w-full text-xs">
              <tbody>
                {rentaEquipoData.microfonoQty > 0 && rentaEquipoData.microfonoDias > 0 && (
                  <tr><td className="py-1 text-slate-600">Micrófono Shure ({rentaEquipoData.microfonoQty} × {rentaEquipoData.microfonoDias} días)</td>
                  <td className="py-1 text-right font-medium text-slate-800">{formatPrice((rentaEquipoData.microfonoQty || 0) * (rentaEquipoData.microfonoDias || 0) * 497)}</td></tr>
                )}
                {rentaEquipoData.camaraDias > 0 && (
                  <tr><td className="py-1 text-slate-600">Cámara Canon ({rentaEquipoData.camaraDias} días)</td>
                  <td className="py-1 text-right font-medium text-slate-800">{formatPrice(rentaEquipoData.camaraDias * 1850)}</td></tr>
                )}
                {rentaEquipoData.lavalierQty > 0 && rentaEquipoData.lavalierDias > 0 && (
                  <tr><td className="py-1 text-slate-600">Lavalier Sennheiser ({rentaEquipoData.lavalierQty} × {rentaEquipoData.lavalierDias} días)</td>
                  <td className="py-1 text-right font-medium text-slate-800">{formatPrice(rentaEquipoData.lavalierQty * rentaEquipoData.lavalierDias * 750)}</td></tr>
                )}
                {rentaEquipoData.teleprompterDias > 0 && (
                  <tr><td className="py-1 text-slate-600">Teleprompter ({rentaEquipoData.teleprompterDias} días)</td>
                  <td className="py-1 text-right font-medium text-slate-800">{formatPrice(rentaEquipoData.teleprompterDias * 897)}</td></tr>
                )}
                <tr className="border-t border-slate-200"><td className="py-2 font-semibold text-slate-700">Subtotal Renta de Equipo</td>
                <td className="py-2 text-right font-bold text-slate-800">{formatPrice(rentaEquipoTotal)}</td></tr>
              </tbody>
            </table>
          </div>
        )}
        {hasTransmision && (
          <div className="border-t border-slate-200 pt-4 mb-4">
            <h3 className="text-sm font-bold text-slate-800 mb-3">Transmisión en Vivo</h3>
            <table className="w-full text-xs">
              <tbody>
                {transmisionData.basicQty > 0 && (
                  <tr><td className="py-1 text-slate-600">Basic Uniplataforma (×{transmisionData.basicQty})</td>
                  <td className="py-1 text-right font-medium text-slate-800">{formatPrice(transmisionData.basicQty * 697)}</td></tr>
                )}
                {transmisionData.proDias > 0 && (
                  <tr><td className="py-1 text-slate-600">Pro Multiplataforma ({transmisionData.proDias} días)</td>
                  <td className="py-1 text-right font-medium text-slate-800">{formatPrice(transmisionData.proDias * 3997)}</td></tr>
                )}
                <tr className="border-t border-slate-200"><td className="py-2 font-semibold text-slate-700">Subtotal Transmisión</td>
                <td className="py-2 text-right font-bold text-slate-800">{formatPrice(transmisionTotal)}</td></tr>
              </tbody>
            </table>
          </div>
        )}
        {hasSonidoDirecto && (
          <div className="border-t border-slate-200 pt-4 mb-4">
            <h3 className="text-sm font-bold text-slate-800 mb-3">Sonido Directo</h3>
            <table className="w-full text-xs">
              <tbody>
                {sonidoDirectoData.basicProHoras > 0 && (
                  <tr><td className="py-1 text-slate-600">BásicoPro ({sonidoDirectoData.basicProHoras} hrs)</td>
                  <td className="py-1 text-right font-medium text-slate-800">{formatPrice(sonidoDirectoData.basicProHoras * 350)}</td></tr>
                )}
                {sonidoDirectoData.proHoras > 0 && (
                  <tr><td className="py-1 text-slate-600">Pro ({sonidoDirectoData.proHoras} hrs)</td>
                  <td className="py-1 text-right font-medium text-slate-800">{formatPrice(sonidoDirectoData.proHoras * 997)}</td></tr>
                )}
                <tr className="border-t border-slate-200"><td className="py-2 font-semibold text-slate-700">Subtotal Sonido Directo</td>
                <td className="py-2 text-right font-bold text-slate-800">{formatPrice(sonidoDirectoTotal)}</td></tr>
              </tbody>
            </table>
          </div>
        )}
        {hasForo && (
          <div className="border-t border-slate-200 pt-4 mb-4">
            <h3 className="text-sm font-bold text-slate-800 mb-3">Foro</h3>
            <table className="w-full text-xs">
              <tbody>
                {foroData.santaFeHoras > 0 && (
                  <tr><td className="py-1 text-slate-600">Foro Santa Fe ({foroData.santaFeHoras} hrs)</td>
                  <td className="py-1 text-right font-medium text-slate-800">{formatPrice(foroData.santaFeHoras * 1000)}</td></tr>
                )}
                {foroData.masivoDias > 0 && (
                  <tr><td className="py-1 text-slate-600">Foro Masivo ({foroData.masivoDias} días)</td>
                  <td className="py-1 text-right font-medium text-slate-800">{formatPrice(foroData.masivoDias * 30000)}</td></tr>
                )}
                {foroData.storageDias > 0 && (
                  <tr><td className="py-1 text-slate-600">Storage ({foroData.storageDias} días)</td>
                  <td className="py-1 text-right font-medium text-slate-800">{formatPrice(foroData.storageDias * 97)}</td></tr>
                )}
                <tr className="border-t border-slate-200"><td className="py-2 font-semibold text-slate-700">Subtotal Foro</td>
                <td className="py-2 text-right font-bold text-slate-800">{formatPrice(foroTotal)}</td></tr>
              </tbody>
            </table>
          </div>
        )}
        {hasInterpretacion && (
          <div className="border-t border-slate-200 pt-4 mb-4">
            <h3 className="text-sm font-bold text-slate-800 mb-3">Interpretación Simultánea</h3>
            <table className="w-full text-xs">
              <tbody>
                {interpretacionData.horas > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">
                      Intérprete Bilingüe ({interpretacionData.horas < 4 ? 4 : interpretacionData.horas} hrs × {interpretacionData.dias || 1} día{(interpretacionData.dias || 1) > 1 ? 's' : ''})
                    </td>
                    <td className="py-1 text-right font-medium text-slate-800">{formatPrice((interpretacionData.horas < 4 ? 4 : interpretacionData.horas) * 1500 * (interpretacionData.dias || 1))}</td>
                  </tr>
                )}
                {interpretacionData.cabina && (
                  <tr>
                    <td className="py-1 text-slate-600">Cabina de audio ({interpretacionData.cabinaDias || interpretacionData.dias || 1} días)</td>
                    <td className="py-1 text-right font-medium text-slate-800">{formatPrice(7500 * (interpretacionData.cabinaDias || interpretacionData.dias || 1))}</td>
                  </tr>
                )}
                {(interpretacionData.equipoTxQty || 0) > 0 && (interpretacionData.equipoTxDias || 0) > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">Equipo TX/RX portátil ({interpretacionData.equipoTxQty} × {interpretacionData.equipoTxDias} días)</td>
                    <td className="py-1 text-right font-medium text-slate-800">{formatPrice(450 * interpretacionData.equipoTxQty * interpretacionData.equipoTxDias)}</td>
                  </tr>
                )}
                {(interpretacionData.tecnicoDias || 0) > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">Técnico de audio ({interpretacionData.tecnicoDias} días)</td>
                    <td className="py-1 text-right font-medium text-slate-800">{formatPrice(1500 * interpretacionData.tecnicoDias)}</td>
                  </tr>
                )}
                <tr className="border-t border-slate-200">
                  <td className="py-2 font-semibold text-slate-700">Subtotal Interpretación</td>
                  <td className="py-2 text-right font-bold text-slate-800">{formatPrice(interpretacionTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {hasSonorizacion && (
          <div className="border-t border-slate-200 pt-4 mb-4">
            <h3 className="text-sm font-bold text-slate-800 mb-3">Sonorización para Eventos</h3>
            <table className="w-full text-xs">
              <tbody>
                <tr>
                  <td className="py-1 text-slate-600">
                    Paquete {sonorizacionData.package === 'sono_essential' ? 'Essential' : sonorizacionData.package === 'sono_plus' ? 'Plus' : 'Premium'} ({sonorizacionData.dias} día{sonorizacionData.dias > 1 ? 's' : ''})
                  </td>
                  <td className="py-1 text-right font-medium text-slate-800">{formatPrice(sonorizacionTotal)}</td>
                </tr>
                <tr className="border-t border-slate-200">
                  <td className="py-2 font-semibold text-slate-700">Subtotal Sonorización</td>
                  <td className="py-2 text-right font-bold text-slate-800">{formatPrice(sonorizacionTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Independent Addons Section (when no audiobook) */}
        {hasIndependentAddons && (
          <div className={`${hasPodcast || hasMeditaciones || hasAudiocursos || hasWalkingPodcast ? 'border-t border-slate-200 pt-4' : ''} mb-4`}>
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-4 h-4 text-[#aa2aff]" />
              <h3 className="text-sm font-bold text-slate-800" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Servicios Adicionales
              </h3>
            </div>
            
            <table className="w-full text-xs">
              <tbody>
                {distributionAddon?.price > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">{distributionAddon.name}</td>
                    <td className="py-1 text-right font-medium text-slate-800">
                      {formatPrice(distributionAddon.price)}
                    </td>
                  </tr>
                )}
                {booktrailerAddon?.price > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">{booktrailerAddon.name}</td>
                    <td className="py-1 text-right font-medium text-slate-800">
                      {formatPrice(booktrailerAddon.price)}
                    </td>
                  </tr>
                )}
                {legalKitAddon?.price > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">{legalKitAddon.name}</td>
                    <td className="py-1 text-right font-medium text-slate-800">
                      {formatPrice(legalKitAddon.price)}
                    </td>
                  </tr>
                )}
                {coverDesignAddon?.price > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">{coverDesignAddon.name}</td>
                    <td className="py-1 text-right font-medium text-slate-800">
                      {formatPrice(coverDesignAddon.price)}
                    </td>
                  </tr>
                )}
                {brandingPackAddon?.price > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">{brandingPackAddon.name}</td>
                    <td className="py-1 text-right font-medium text-slate-800">
                      {formatPrice(brandingPackAddon.price)}
                    </td>
                  </tr>
                )}
                {musicStockAddon?.price > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">{musicStockAddon.name}</td>
                    <td className="py-1 text-right font-medium text-slate-800">
                      {formatPrice(musicStockAddon.price)}
                    </td>
                  </tr>
                )}
                {consultoriaAddon?.price > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">{consultoriaAddon.name}</td>
                    <td className="py-1 text-right font-medium text-slate-800">
                      {formatPrice(consultoriaAddon.price)}
                    </td>
                  </tr>
                )}
                {qrDinamicoAddon?.price > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">{qrDinamicoAddon.name}</td>
                    <td className="py-1 text-right font-medium text-slate-800">{formatPrice(qrDinamicoAddon.price)}</td>
                  </tr>
                )}
                {enlaceGoAddon?.price > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">{enlaceGoAddon.name}</td>
                    <td className="py-1 text-right font-medium text-slate-800">{formatPrice(enlaceGoAddon.price)}</td>
                  </tr>
                )}
                {nfcVcardAddon?.price > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">{nfcVcardAddon.name}</td>
                    <td className="py-1 text-right font-medium text-slate-800">{formatPrice(nfcVcardAddon.price)}</td>
                  </tr>
                )}
                {separadoresAddon?.price > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">{separadoresAddon.name}</td>
                    <td className="py-1 text-right font-medium text-slate-800">{formatPrice(separadoresAddon.price)}</td>
                  </tr>
                )}
                {preventaAddon?.price > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">{preventaAddon.name}</td>
                    <td className="py-1 text-right font-medium text-slate-800">{formatPrice(preventaAddon.price)}</td>
                  </tr>
                )}
                {bannerPromotionAddon?.price > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">{bannerPromotionAddon.name}</td>
                    <td className="py-1 text-right font-medium text-slate-800">{formatPrice(bannerPromotionAddon.price)}</td>
                  </tr>
                )}
                {disenoWebBannerAddon?.price > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">{disenoWebBannerAddon.name}</td>
                    <td className="py-1 text-right font-medium text-slate-800">{formatPrice(disenoWebBannerAddon.price)}</td>
                  </tr>
                )}
                {photoshootAddon?.price > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">{photoshootAddon.name}</td>
                    <td className="py-1 text-right font-medium text-slate-800">{formatPrice(photoshootAddon.price)}</td>
                  </tr>
                )}
                {paginaAutorAddon?.price > 0 && (
                  <tr>
                    <td className="py-1 text-slate-600">{paginaAutorAddon.name}</td>
                    <td className="py-1 text-right font-medium text-slate-800">{formatPrice(paginaAutorAddon.price)}</td>
                  </tr>
                )}
                <tr className="border-t border-slate-200">
                  <td className="py-2 font-semibold text-slate-700">Subtotal Servicios</td>
                  <td className="py-2 text-right font-bold text-slate-800">
                    {formatPrice(addonsTotal)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* GRAN TOTAL */}
        <div className="border-t-2 border-slate-800 bg-gradient-to-r from-[#aa2aff]/5 to-[#272b96]/5 -mx-5 px-5 py-4 mt-4">

          {/* Editorial Services section */}
          {editorialData && editorialData.subtotal > 0 && (
            <div className="mb-3">
              <div className="bg-gradient-to-r from-[#272b96]/10 to-[#aa2aff]/10 rounded-lg px-3 py-2 mb-2 border border-[#272b96]/20">
                <h4 className="text-[#272b96] font-bold text-xs tracking-wide">SERVICIOS EDITORIALES</h4>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {editorialData.maquetacion && (
                    <tr>
                      <td className="py-1 text-slate-600">Maquetación físico y digital</td>
                      <td className="py-1 text-right">{renderPrice(editorialData.maquetacionTotal)}</td>
                    </tr>
                  )}
                  {editorialData.correccion && editorialData.correccionTotal > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">Corrección de estilo ({editorialData.correccionCuartillas} cuartillas)</td>
                      <td className="py-1 text-right">{renderPrice(editorialData.correccionTotal)}</td>
                    </tr>
                  )}
                  {editorialData.portadaFisica && (
                    <tr>
                      <td className="py-1 text-slate-600">Diseño portada libro físico</td>
                      <td className="py-1 text-right">{renderPrice(editorialData.portadaFisicaTotal)}</td>
                    </tr>
                  )}
                  {editorialData.impresion && editorialData.impresionTotal > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">Impresión ({editorialData.impresionCantidad} libros)</td>
                      <td className="py-1 text-right">{renderPrice(editorialData.impresionTotal)}</td>
                    </tr>
                  )}
                  {editorialData.ghostWriting && editorialData.ghostWritingTotal > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">Ghost Writing</td>
                      <td className="py-1 text-right">{renderPrice(editorialData.ghostWritingTotal)}</td>
                    </tr>
                  )}
                  <tr className="border-t border-slate-200">
                    <td className="py-2 font-semibold text-slate-700">Subtotal Editorial</td>
                    <td className="py-2 text-right font-bold text-slate-800">
                      {formatPrice(editorialData.subtotal)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          
          {/* Voice Services Section */}
          {voiceServicesTotal > 0 && (
            <div className="mb-3">
              <h3 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                <Mic className="w-4 h-4 text-[#aa2aff]" /> Servicios de Voz
              </h3>
              <table className="w-full text-xs">
                <tbody>
                  {VOICE_CATEGORIES.map(cat =>
                    cat.items.filter(item => (voiceServicesQuantities[item.id] || 0) > 0).map(item => {
                      const qty = voiceServicesQuantities[item.id];
                      const lineTotal = item.price * qty;
                      return (
                        <tr key={item.id}>
                          <td className="py-1 text-slate-600">{item.name} {qty > 1 ? `(×${qty})` : ''}</td>
                          <td className="py-1 text-right">{renderPrice(lineTotal)}</td>
                        </tr>
                      );
                    })
                  )}
                  <tr className="border-t border-slate-200">
                    <td className="py-1 font-semibold text-slate-700">Subtotal Servicios de Voz</td>
                    <td className="py-1 text-right font-bold text-slate-800">{formatPrice(voiceServicesTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Studio Fees Section */}
          {studioFeesTotal > 0 && (
            <div className="mb-3">
              <h3 className="text-sm font-bold text-slate-700 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Studio Fees
              </h3>
              <table className="w-full text-xs">
                <tbody>
                  {(studioHours.premium || 0) > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">Studio Premium ({studioHours.premium}h)</td>
                      <td className="py-1 text-right">{renderPrice(studioHours.premium * (settings?.price_studio_premium || 3500))}</td>
                    </tr>
                  )}
                  {(studioHours.essential || 0) > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">Studio Essential ({studioHours.essential}h)</td>
                      <td className="py-1 text-right">{renderPrice(studioHours.essential * (settings?.price_studio_essential || 2000))}</td>
                    </tr>
                  )}
                  {audioPostHours > 0 && (
                    <tr>
                      <td className="py-1 text-slate-600">AudioPost ({audioPostHours}h)</td>
                      <td className="py-1 text-right">{renderPrice(audioPostHours * (settings?.price_audio_post || 1500))}</td>
                    </tr>
                  )}
                  <tr className="border-t border-slate-200">
                    <td className="py-1 font-semibold text-slate-700">Subtotal Studio Fees</td>
                    <td className="py-1 text-right font-bold text-slate-800">{formatPrice(studioFeesTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Hand-Free Section */}
          {handFreeTotal > 0 && (
            <div className="mb-3">
              <h3 className="text-sm font-bold text-slate-700 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Hand-Free
              </h3>
              <table className="w-full text-xs">
                <tbody>
                  {handFreeRows.filter(r => r.unitPrice > 0 && r.quantity > 0).map(row => (
                    <tr key={row.id}>
                      <td className="py-1 text-slate-600">{row.description || 'Servicio'} ({row.quantity} {row.unit})</td>
                      <td className="py-1 text-right">{renderPrice(row.unitPrice * row.quantity)}</td>
                    </tr>
                  ))}
                  <tr className="border-t border-slate-200">
                    <td className="py-1 font-semibold text-slate-700">Subtotal Hand-Free</td>
                    <td className="py-1 text-right font-bold text-slate-800">{formatPrice(handFreeTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* ViubuxConnect rows - near subtotal */}
          {viubuxConnectActive && (
            <>
              <div className="flex justify-between items-center py-2">
                <span className="text-[#00c2ff] font-medium text-sm">
                  Inscripción ViubuxConnect
                </span>
                <span className="text-[#00c2ff] font-bold">
                  +{formatPrice(viubuxConnectInscription)}
                </span>
              </div>
              {vcCatalogoCost > 0 && (
                <div className="flex justify-between items-center py-2">
                  <span className="text-[#00c2ff] font-medium text-sm">
                    12 meses Catálogo ViubuxConnect
                  </span>
                  <span className="text-[#00c2ff] font-bold">
                    +{formatPrice(vcCatalogoCost)}
                  </span>
                </div>
              )}
              {viubuxConnectDiscountAmount > 0 && !showDiscountPerItem && (
                <div className="flex justify-between items-center py-2 border-b border-slate-200">
                  <span className="text-green-600 font-medium text-sm">
                    Descuento ViubuxConnect (15%)
                  </span>
                  <span className="text-green-600 font-bold">
                    -{formatPrice(viubuxConnectDiscountAmount)}
                  </span>
                </div>
              )}
            </>
          )}

          {/* Custom discount row */}
          {customDiscount > 0 && (
            <div className="flex justify-between items-center py-2 mb-2 border-b border-slate-200">
              <span className="text-green-600 font-medium text-sm">
                Descuento especial {customDiscountType === 'fixed' ? `(${formatPrice(customDiscount)})` : `(${customDiscount}%)`}
              </span>
              <span className="text-green-600 font-bold">
                -{formatPrice(customDiscountAmount)}
              </span>
            </div>
          )}
          
          {/* Subtotal before IVA */}
          <div className="flex justify-between items-center py-2 border-b border-slate-200">
            <span className="text-slate-600 text-sm">Subtotal</span>
            <span className="text-slate-800 font-medium">
              {formatPrice(subtotalAfterDiscount)}
            </span>
          </div>
          
          {/* IVA (16%) */}
          <div className="flex justify-between items-center py-2 border-b border-slate-200">
            <span className="text-slate-600 text-sm">IVA (16%)</span>
            <span className="text-slate-800 font-medium">
              +{formatPrice(ivaAmount)}
            </span>
          </div>

          {/* Final Grand Total */}
          <div className="flex justify-between items-center pt-3">
            <div>
              <span className="text-base font-bold text-slate-800" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                TOTAL A INVERTIR
              </span>
            </div>
            <div className="text-right">
              <span className={`text-2xl font-bold ${showCoproduccion ? 'text-slate-400 line-through text-lg' : 'text-[#aa2aff]'}`} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {formatPrice(finalTotalWithCustomDiscount)}
              </span>
              <span className="text-slate-500 text-sm ml-1">{currency}</span>
            </div>
          </div>
          
          {/* Co-Producción */}
          {showCoproduccion && coproduccionAmount > 0 && (
            <>
              <div className="flex justify-between items-center py-2 mt-2 border-t border-amber-300/30">
                <span className="text-amber-600 font-medium text-sm">
                  Aportación Co-Inversionista ({coproduccionPercent}%)
                </span>
                <span className="text-amber-600 font-bold">
                  -{formatPrice(coproduccionAmount)}
                </span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-slate-300">
                <div>
                  <span className="text-base font-bold text-slate-800" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    TU INVERSIÓN
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-bold text-[#aa2aff]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {formatPrice(clientPayAmount)}
                  </span>
                  <span className="text-slate-500 text-sm ml-1">{currency}</span>
                </div>
              </div>
            </>
          )}

          {/* Payment Plan Options */}
          {(() => {
            const payable = (showCoproduccion && coproduccionAmount > 0) ? (clientPayAmount || 0) : finalTotalWithCustomDiscount;
            if (payable <= 0) return null;
            const withSurcharge = payable * 1.16;
            const perInstallment = withSurcharge / 3;
            return (
              <div className="mt-4 border-t border-slate-200 pt-3" data-testid="quote-payment-options">
                <p className="text-xs font-bold text-slate-700 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Opciones de Pago
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-2 rounded-lg border border-slate-200 bg-slate-50">
                    <p className="text-[10px] uppercase tracking-wide text-slate-500">Contado (1 exhibición)</p>
                    <p className="text-base font-bold text-slate-800">{formatPrice(payable)} <span className="text-xs text-slate-500 font-normal">{currency}</span></p>
                  </div>
                  <div className="p-2 rounded-lg border border-[#aa2aff]/30 bg-[#aa2aff]/5">
                    <p className="text-[10px] uppercase tracking-wide text-[#aa2aff]">3 pagos (+16%)</p>
                    <p className="text-base font-bold text-[#aa2aff]">3 × {formatPrice(perInstallment)}</p>
                    <p className="text-[10px] text-slate-500">Total con recargo: {formatPrice(withSurcharge)} {currency}</p>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Exchange Rate Note (USD only) */}
        {currency === 'USD' && (
          <div className="mt-3 p-2 bg-blue-50 rounded-lg border border-blue-200 text-center">
            <span className="text-xs font-semibold text-[#272b96]">
              Tipo de Cambio aplicado: 1 USD = ${Number(exchangeRate).toFixed(2)} MXN
            </span>
          </div>
        )}

        {/* Footer */}
        <div className="mt-4 pt-4 border-t border-slate-200 text-center">
          <p className="text-xs text-slate-400">
            Cotización válida hasta el {validUntil}
          </p>
        </div>
      </div>
    </div>
  );
});

QuotePreview.displayName = 'QuotePreview';

export default QuotePreview;
